import React, { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import {
  DropdownSingle,
  TextInput,
  PasswordInput,
  colors2,
  UI_BODY_SM_DF,
  DateRangePicker,
} from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
  EmptyBanner,
} from "./BaseIntegrationInterface";
import { useOAuthConnection } from "../../hooks/useOAuthConnection";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// KoboToolbox has no OAuth for third-party apps: the user pastes a personal
// API token (Account Settings > Security) and picks the server the account
// lives on. The BE verifies the token against /me/, stores it encrypted, and
// the card gets the same Connect / Connected / Stale lifecycle as OAuth
// providers. providerAccountId holds the server URL, providerAccountName the
// Kobo username.
//
// One dataset imports exactly one form. A dataset that already exists has its
// form pinned in cronConfig.scope.formUid, and the BE rejects a preview that
// asks for a different one.

// ============================================
// KOBO ICON
// ============================================

export const KoboIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#2095F3" />
    <path
      fill="#FFFFFF"
      d="M9 7h4v8.2l7.2-8.2h5l-8.4 9.3L26 25h-5.2l-6.3-7.6L13 19.2V25H9z"
    />
  </svg>
);

// ============================================
// CONSTANTS
// ============================================

const SERVER_OPTIONS = [
  { display: "Global (kf.kobotoolbox.org)", value: "https://kf.kobotoolbox.org" },
  { display: "EU (eu.kobotoolbox.org)", value: "https://eu.kobotoolbox.org" },
  { display: "Custom / self-hosted server", value: "custom" },
];

const SCOPE_OPTIONS = [
  { display: "All submissions", value: "all" },
  { display: "Submitted within a date range", value: "range" },
];

const DAY_MS = 24 * 60 * 60 * 1000;
const LOCAL_HOSTS = ["localhost", "127.0.0.1"];

const isoDaysAgo = (n) => new Date(Date.now() - n * DAY_MS).toISOString().slice(0, 10);
const todayIso = () => new Date().toISOString().slice(0, 10);

// FE mirror of providers/kobo.js normaliseServerUrl, used only to enable the
// Connect button early. The BE stays the authority and rejects independently.
const normaliseServerUrlClient = (raw) => {
  let s = String(raw || "").trim();
  if (!s) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) s = `https://${s}`;
  let u;
  try {
    u = new URL(s);
  } catch {
    return null;
  }
  if (u.username || u.password) return null;
  const isLocal = LOCAL_HOSTS.includes(u.hostname);
  if (u.protocol !== "https:" && !(u.protocol === "http:" && isLocal)) return null;
  return `${u.protocol}//${u.host}`;
};

// Which dropdown option a stored server URL corresponds to.
const serverOptionFor = (storedUrl) =>
  SERVER_OPTIONS.find((o) => o.value === storedUrl) || SERVER_OPTIONS[2];

// ============================================
// STYLED COMPONENTS
// ============================================

const FieldGroup = styled.div`
  margin-top: 16px;
`;

const Hint = styled(UI_BODY_SM_DF)`
  color: ${({ $error }) => ($error ? "#c0392b" : colors2.grey60)};
  margin-top: 8px;
`;

// ============================================
// INTERFACE
// ============================================

export const KoboInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
  submitError,
  onClearSubmitError,
  contentType,
}) => {
  const {
    connection,
    connectionLoading,
    connectError,
    preflightData,
    preflightError,
    isPreflightLoading,
    connectWithToken,
    isConnectingWithToken,
    handleDisconnect,
  } = useOAuthConnection({ providerId: "kobo", providerName: "KoboToolbox" });

  // Token form (not connected / stale).
  const [serverOption, setServerOption] = useState(SERVER_OPTIONS[0]);
  const [customUrl, setCustomUrl] = useState("");
  const [token, setToken] = useState("");

  // Import picker (connected).
  const [selectedForm, setSelectedForm] = useState(null);
  const [scope, setScope] = useState(SCOPE_OPTIONS[0]);
  const [dateRange, setDateRange] = useState([isoDaysAgo(30), todayIso()]);
  const [startDate, endDate] = dateRange;

  // Stale reconnect: pre-seed the server from the stored connection so the
  // user only retypes the token.
  useEffect(() => {
    const stored = connection?.providerAccountId;
    if (!stored) return;
    const opt = serverOptionFor(stored);
    setServerOption(opt);
    if (opt.value === "custom") setCustomUrl(stored);
  }, [connection?.providerAccountId]);

  const resolvedServerUrl =
    serverOption?.value === "custom" ? normaliseServerUrlClient(customUrl) : serverOption?.value;
  const customUrlInvalid = serverOption?.value === "custom" && customUrl.trim() && !resolvedServerUrl;
  const canConnect = !!resolvedServerUrl && token.trim().length > 0 && !isConnectingWithToken;

  const onConnectClick = async () => {
    if (!canConnect) return;
    const ok = await connectWithToken({
      apiToken: token.trim(),
      providerParams: { serverUrl: resolvedServerUrl },
    });
    if (ok) setToken("");
  };

  const forms = preflightData?.forms || [];
  const formOptions = forms.map((f) => ({
    display: `${f.name} (${f.submissionCount} submission${f.submissionCount === 1 ? "" : "s"})${f.deployed ? "" : " — archived"}`,
    value: f.uid,
    _form: f,
  }));

  // `connection` is the raw /status response, so the dataset's pinned form has
  // to be read off its own link row. Only set when appending to an existing
  // dataset; a brand new dataset is free to pick any form.
  const storedFormUid = contentType
    ? connection?.links?.find((l) => l.contentType === contentType)?.cronConfig?.scope?.formUid
    : null;
  const storedFormOption = storedFormUid
    ? formOptions.find((o) => o.value === storedFormUid)
    : null;
  const storedFormMissing = !!storedFormUid && !isPreflightLoading && !storedFormOption;

  useEffect(() => {
    if (!storedFormOption) return;
    setSelectedForm((curr) => (curr?.value === storedFormOption.value ? curr : storedFormOption));
  }, [storedFormUid, storedFormOption]);

  const rangeInverted = startDate && endDate && new Date(startDate) > new Date(endDate);
  const canSubmit =
    connection?.connected &&
    connection?.status !== "stale" &&
    !!selectedForm &&
    !storedFormMissing &&
    (scope?.value === "all" || (startDate && endDate && !rangeInverted));

  const submitErrorMessage =
    submitError && submitError.sourceId === "kobo" ? submitError.message : null;
  const isEmptyResult =
    submitErrorMessage && /no kobotoolbox submissions/i.test(submitErrorMessage);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onClearSubmitError && onClearSubmitError();
    onSubmit({
      adapterOptions: {
        formUid: selectedForm.value,
        ...(scope?.value === "range"
          ? {
              startDate: moment(startDate).startOf("day").toISOString(),
              endDate: moment(endDate).endOf("day").toISOString(),
            }
          : {}),
      },
    });
  };

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await handleDisconnect();
      setSelectedForm(null);
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  // ------- Shared token form (states 1 and 3) -------

  const renderTokenForm = ({ buttonLabel }) => (
    <>
      <FieldGroup>
        <DropdownSingle
          label="KoboToolbox server"
          options={SERVER_OPTIONS}
          value={serverOption}
          onChange={(k, v) => setServerOption(v || SERVER_OPTIONS[0])}
          readOnly={isConnectingWithToken}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </FieldGroup>
      {serverOption?.value === "custom" && (
        <FieldGroup>
          <TextInput
            label="Server URL"
            placeholder="https://kobo.example.org"
            value={customUrl}
            onChange={(k, v) => setCustomUrl(v)}
          />
          {customUrlInvalid && (
            <Hint $error>Enter the full https:// address of your KoboToolbox server.</Hint>
          )}
        </FieldGroup>
      )}
      <FieldGroup>
        <PasswordInput
          id="kobo-api-token"
          label="API token"
          placeholder="Paste your KoboToolbox API token"
          value={token}
          onChange={(k, v) => setToken(v)}
        />
      </FieldGroup>

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}

      <InterfaceActions>
        <ButtonPrimary onClick={onConnectClick} disabled={!canConnect} isLoading={isConnectingWithToken}>
          {buttonLabel}
        </ButtonPrimary>
      </InterfaceActions>

      <HelpText>
        Find your token on your Kobo server under <strong>Account Settings → Security</strong>
        {" "}(click <strong>Display</strong> next to API key). The token gives read access to every
        project in that account; Dots stores it encrypted and only reads submissions.
      </HelpText>
    </>
  );

  // ------- Render -------

  if (connectionLoading) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to KoboToolbox</InterfaceHeader>
        <InterfaceDescription>Checking connection status…</InterfaceDescription>
      </InterfaceContainer>
    );
  }

  // State 1: not connected — collect server + token.
  if (!connection?.connected) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to KoboToolbox</InterfaceHeader>
        <InterfaceDescription>
          Import form submissions from KoboToolbox. Choose the server your account lives on
          and paste your API token; you will then pick which forms to import.
        </InterfaceDescription>
        {renderTokenForm({ buttonLabel: "Connect KoboToolbox" })}
      </InterfaceContainer>
    );
  }

  // State 3: stale — same form, server pre-seeded, new token.
  if (connection.status === "stale") {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Reconnect KoboToolbox</InterfaceHeader>
        <StaleBanner>
          <ConnectedMeta>
            <strong>⚠ Connection needs to be renewed</strong>
            <span>
              {connection.providerAccountId
                ? `KoboToolbox at ${connection.providerAccountId} rejected the stored token. Paste a current token to reconnect.`
                : "KoboToolbox rejected the stored token. Paste a current token to reconnect."}
            </span>
          </ConnectedMeta>
        </StaleBanner>
        {renderTokenForm({ buttonLabel: "Reconnect KoboToolbox" })}
      </InterfaceContainer>
    );
  }

  // State 2: connected — pick forms and an optional date range.
  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from KoboToolbox</InterfaceHeader>
      <InterfaceDescription>
        Each submission becomes one row and each question becomes a column.
        One dataset holds one form; import another form as a new dataset.
      </InterfaceDescription>

      <ConnectedBanner>
        <ConnectedMeta>
          <strong>
            ✓ Connected{connection.providerAccountName ? ` as ${connection.providerAccountName}` : ""}
          </strong>
          {connection.providerAccountId && (
            <span style={{ color: colors2.grey60 }}>{connection.providerAccountId}</span>
          )}
          {connection.authorizedBy?.name && (
            <span style={{ color: colors2.grey60 }}>Connected by: {connection.authorizedBy.name}</span>
          )}
        </ConnectedMeta>
        <ButtonSecondary onClick={() => setShowDisconnectConfirm(true)} disabled={isLoading}>
          Disconnect
        </ButtonSecondary>
      </ConnectedBanner>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name="KoboToolbox"
          onConfirm={confirmDisconnect}
          onClose={() => setShowDisconnectConfirm(false)}
          isLoading={isDisconnecting}
        />
      )}

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      {preflightError && <ErrorBanner>{preflightError}</ErrorBanner>}
      {submitErrorMessage && isEmptyResult && <EmptyBanner>{submitErrorMessage}</EmptyBanner>}
      {submitErrorMessage && !isEmptyResult && <ErrorBanner>{submitErrorMessage}</ErrorBanner>}

      <FieldGroup>
        <DropdownSingle
          label={adapterConfig?.preflightConfig?.selectLabel || "Select form"}
          placeholder={
            isPreflightLoading
              ? "Loading forms..."
              : adapterConfig?.preflightConfig?.selectPlaceholder || "Choose a form to import..."
          }
          options={formOptions}
          value={selectedForm}
          onChange={(k, v) => setSelectedForm(v || null)}
          readOnly={isPreflightLoading || isLoading || !!storedFormUid}
          isSearchable={true}
          searchbarPlaceholder="Search forms..."
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
        {storedFormMissing ? (
          <Hint $error>
            The form this dataset imports is no longer available in this KoboToolbox account.
          </Hint>
        ) : storedFormUid ? (
          <Hint>
            This dataset imports one form; pick a different form by creating a new dataset.
          </Hint>
        ) : null}
        {!storedFormUid && !isPreflightLoading && !preflightError && forms.length === 0 && (
          <Hint>No deployed forms found in this KoboToolbox account.</Hint>
        )}
      </FieldGroup>

      <FieldGroup>
        <DropdownSingle
          label="Which submissions"
          options={SCOPE_OPTIONS}
          value={scope}
          onChange={(k, v) => setScope(v || SCOPE_OPTIONS[0])}
          readOnly={isLoading}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </FieldGroup>

      {scope?.value === "range" && (
        <FieldGroup>
          <DateRangePicker
            id="kobo-date-range"
            label="Submitted between"
            value={dateRange}
            onChange={(k, v) => setDateRange(v)}
            previousDateOnly
            readOnly={isLoading}
          />
          {rangeInverted && <Hint $error>End date must be on or after start date.</Hint>}
        </FieldGroup>
      )}

      <InterfaceActions>
        <ButtonPrimary onClick={handleSubmit} disabled={!canSubmit || isPreflightLoading || isLoading} isLoading={isLoading}>
          {adapterConfig?.preflightConfig?.submitLabel || "Import Submissions"}
        </ButtonPrimary>
      </InterfaceActions>
    </InterfaceContainer>
  );
};

export const koboIntegrationMeta = {
  id: "kobo",
  name: "KoboToolbox",
  description: "Import form submissions from KoboToolbox",
};

export default KoboInterface;
